import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {plainToClass} from 'class-transformer';
import {SelectItem} from 'primeng/api';
import {Observable} from 'rxjs';
import {map} from "rxjs/operators";

import {GrowlService, LocalCacheService} from "@app/core";

import {RoundUpDecimalPlace} from "@app/shared/classes/common";
import {AbstractItem, ItemCatalog, ItemSearchParam, TableFooterSummary} from '@app/shared/classes/item';
import {Bom, PurchaseRequisitionItem} from '@app/shared/classes/purchase-req';
import {VendorInvoiceItem} from "@app/shared/classes/vendor-invoice";
import {SalesOrderItem} from "@app/shared/classes/sales-order";
import {CustomerInvoiceItem} from "@app/shared/classes/customer-invoice-item";
import {PackingSlipItem} from "@app/shared/classes/packing-slip-item";
import {ReceiptItem} from "@app/shared/classes/receipt";
import {PurchaseReturnChargeCode, ReturnItem} from "@app/shared/classes/purchase-return";
import {PurchaseChargeCode} from "@app/shared/classes/charge-code";
import {SalesReturnItem} from "@app/shared/classes/sales-return-item";
import {VendorInvoiceChargeCode} from "@app/shared/classes/vendor-invoice-charge-code";

import {ItemType} from '@app/shared/enums/item-type.enum';
import {RevenueType} from '@app/shared/enums/revenue-type.enum';
import {Transaction} from '@app/shared/enums/txn-type.enum';
import {GrowlType} from "@app/shared/enums/growl-type.enum";

import {CommonDataService} from '@app/shared/services/common-data.service';
import {CommonTransformationService} from '@app/shared/services/common-transformation.service';
import {MathService} from '@app/shared/services/math.service';
import * as roundTo from "round-to";
import {AccountMappingType, QueryResult} from "@app/shared";
import {ItemHistorySummary} from "@app/item-catalog/item-catalog";

@Injectable()
export class ItemCatalogService {

  constructor(private http: HttpClient, private transform: CommonTransformationService, private commonDataService: CommonDataService, private mathService: MathService,
              private growl: GrowlService, private cache: LocalCacheService) {

    // cache sales tax item from the backend
    if (!this.cache.salesTaxItem) {
      this.searchApprovedItemsByItemType(ItemType.SalesTax).subscribe(res => {
        this.cache.salesTaxItem = plainToClass(AbstractItem, res.list[0] as AbstractItem);
      });
    }
  }

  private catalogsURL = `/finance/item-catalogs`;
  private catalogsSearchURL = `/finance/item-catalogs/search`;
  private itemsURL = `/finance/items`;
  private itemSearch = `/finance/items/search`;
  private refCodeURL = `/lookup/domains/codes`;
  private enumURL = `/utils/enum-pairs`;
  private allUsersURL = `/directory/users/search?subtype=EMPLOYEE`;
  private itemLocators = `/finance/item-locators/search`;
  private itemHistory = '/finance/items/history';
  private findBomURL = `/finance/items/bom/items`;
  private bomURL = `/finance/items/bom`;

  private accessTypeEnum = 'finance.itemcatalog.type.AccessType';
  private approvalTypeEnum = 'finance.itemcatalog.type.ApprovalType';
  private itemTypeEnum = 'finance.itemcatalog.type.ItemType';


  convertToSalesTaxItem(item: AbstractItem) {
    let salesTaxItem: any = {};

    salesTaxItem.abstractItemId = item.id;
    salesTaxItem.nameOrNumber = item.nameOrNumber;
    salesTaxItem.subtype = item.subtype;
    salesTaxItem.unitOfMeasureId = item.unitOfMeasureId;
    salesTaxItem.unitOfMeasureDescription = item.unitOfMeasureDescription;
    salesTaxItem.taxAmount = 0;

    return salesTaxItem;
  }

  getAllCatalogsDashboard(): Observable<any> {
    const url = `/finance/item-catalogs/dashboard/catalog?`;
    return this.http.get(url);
  }

  getOneCatalogDashboard(catalogId: number): Observable<any> {
    const url = `/finance/item-catalogs/dashboard/catalog?catalogId=${catalogId}`;
    return this.http.get(url);
  }

  searchVendors(orgType: string, businessName: string): Observable<any> {
    const url = `/procurement/trading-partners/search?subtype=${orgType}&businessName=${businessName}`;
    return this.http.get<any>(url);
  }


  getCatalogs(): Observable<ItemCatalog[]> {
    return this.http.get<QueryResult>(this.catalogsURL).pipe(map(response => {
      return plainToClass(ItemCatalog, response.list);
    }));
  }

  getCatalogById(catalogId: number): Observable<ItemCatalog> {
    return this.http.get<any>(`${this.catalogsSearchURL}?id=${catalogId}`).pipe(map(response => {
      return response.list[0];
    }));
  }

  saveCatalog(catalog: ItemCatalog): Observable<ItemCatalog> {
    return this.http.post<ItemCatalog>(`${this.catalogsURL}`, catalog);
  }

  deleteCatalog(catalogId: number): Observable<Response> {
    return this.http.delete<Response>(`${this.catalogsURL}/${catalogId}`);
  }

  updateCatalog(catalogId: number, catalog: ItemCatalog): Observable<ItemCatalog> {
    return this.http.put<ItemCatalog>(`${this.catalogsURL}/${catalogId}`, catalog);
  }

  saveOrUpdateItems(items: AbstractItem[]): Observable<QueryResult> {
    const url = `/finance/items-array`;
    return this.http.post<QueryResult>(url, items);
  }

  saveOrUpdateItem(item: AbstractItem): Observable<AbstractItem> {
    let url = this.itemsURL;
    if (item.id) {
      url += `/${item.id}`;
      return this.http.put<AbstractItem>(url, item);
    } else {
      return this.http.post<AbstractItem>(url, item);
    }
  }

  //this search gives items whose approval status is Pending Status only for the User who has added it to catalog
  searchFilteredItems(catalogId: number, searchString: string): Observable<QueryResult> {
    const url = `/finance/items/filtered-search?catalogId=${catalogId}&search=${searchString}`;

    return this.http.get<QueryResult>(url);
  }

  searchApprovedItemsByName(query: string, approvalType: string): Observable<any> {
    return this.http.get(`${this.itemSearch}?approvalType=${approvalType}&nameOrNumber=${query}`);
  }

  searchApprovedItemsByItemType(itemType: string): Observable<any> {
    const url = `/finance/items/search?subtype=${itemType}`;

    return this.http.get(url);
  }

  getItemsByCatId(catalogId: number): Observable<QueryResult> {
    return this.http.get<QueryResult>(`${this.itemSearch}?catalog.id=${catalogId}&count=-1`);
  }

  deleteItem(itemId: string): Observable<ItemCatalog[]> {
    return this.http.delete<ItemCatalog[]>(`${this.itemsURL}/${itemId}`);
  }

  getAccessTypes(): Observable<any[]> {
    return this.http.get<SelectItem[]>(`${this.enumURL}/?name=${this.accessTypeEnum}`);
  }

  getLocators(id: number): Observable<any> {
    return this.http.get(`${this.itemLocators}?warehouse.id=${id}`);
  }

  searchLocators(locationName: string): Observable<QueryResult> {
    let url = `${this.itemLocators}`;
    if (locationName && locationName.length > 0) {
      url += `?locationName=${locationName}`;
    }
    return this.http.get<QueryResult>(url);
  }

  getWarehousesAsDropdown(addrType: string): Observable<SelectItem[]> {
    let itemWarehouses = `/finance/item-catalogs/warehouse/search`;
    return this.http.get(`${itemWarehouses}?addrType=${addrType}`).pipe(map((res: QueryResult) => {
      return this.transform.convertToDropDown("addrName", res.list, "id");
    }));
  }

  getItemHistory(itemId: number): Observable<ItemHistorySummary[]> {
    return this.http.get<ItemHistorySummary[]>(`${this.itemHistory}/${itemId}?`);
  }

  getItemsByBomId(bomId: number): Observable<QueryResult> {
    return this.http.get<QueryResult>(`${this.findBomURL}/${bomId}?`);
  }

  getItemById(id: number): Observable<any> {
    return this.http.get<any>(`${this.itemsURL}/${id}?`);
  }

  saveOrUpdateBom(bom: Bom): Observable<Bom> {
    let url = this.bomURL;
    if (bom.id) {
      url += `/${bom.id}`;
      return this.http.put<Bom>(url, bom);
    } else {
      return this.http.post<Bom>(url, bom);
    }
  }

  deleteBomItem(bomId: number): Observable<any> {
    return this.http.delete<any>(`${this.bomURL}/${bomId}?`);
  }

  searchItem(searchItem: ItemSearchParam, catalogId?: number): Observable<QueryResult> {
    let query: string = this.transform.buildSearchPanelURL(searchItem);
    if (catalogId) {
      query += `&catalog.id=` + catalogId;
    }
    return this.http.get<QueryResult>(`/finance/items/search?` + query + `&count=-1`);
  }

  searchItemByName(nameString: string): Observable<QueryResult> {
    let url = `${this.itemSearch}`;
    if (nameString && nameString.length > 0) {
      url += `?nameOrNumber=${nameString}`;
    }
    return this.http.get<QueryResult>(url);
  }

  getAllRequiredAccountMappingTypes(): Observable<Map<string, Array<AccountMappingType>>> {
    const url = "/finance/account-mappings/all-item-mapping";
    return this.http.get<Map<string, Array<AccountMappingType>>>(url);
  }

  newItemForTransaction(transaction: Transaction): PurchaseRequisitionItem | VendorInvoiceItem | SalesOrderItem | CustomerInvoiceItem {

    if (transaction == Transaction.PurchaseRequisition) {
      return new PurchaseRequisitionItem();
    } else if (transaction == Transaction.VendorInvoice) {
      return new VendorInvoiceItem();
    } else if (transaction == Transaction.SalesOrder) {
      return new SalesOrderItem();
    } else if (transaction == Transaction.CustomerInvoice) {
      return new CustomerInvoiceItem();
    }
  }

  isUnsupportedTransaction(transaction: Transaction): boolean {
    return !(transaction == Transaction.CustomerInvoice || transaction == Transaction.SalesOrder || transaction == Transaction.PurchaseRequisition ||
      transaction == Transaction.VendorInvoice);
  }

  isSpecialItem(item: PurchaseRequisitionItem | VendorInvoiceItem | SalesOrderItem | CustomerInvoiceItem): boolean {
    return (item.subtype == ItemType.Shipping || item.subtype == ItemType.Discount || item.subtype == ItemType.SalesTax);
  }

  /**
   * @desc remove special items from the input items and return in the special items;
   *       invoked after entity is retrieved from the backend so that items can be displayed correctly
   *       assume there is only one of each special item e.g. not multiple Shipping
   *       NOTE: shipping item are not removed for Receipt
   */
  extractSpecialItems(items: Array<PurchaseRequisitionItem | ReceiptItem | ReturnItem | VendorInvoiceItem | SalesOrderItem | PackingSlipItem | SalesReturnItem | CustomerInvoiceItem>) {

    let specialItems: Array<PurchaseRequisitionItem | ReceiptItem | ReturnItem | VendorInvoiceItem | SalesOrderItem | PackingSlipItem | SalesReturnItem | CustomerInvoiceItem> = [];

    if (items && items.length > 0) {
      const shipIndex = items.findIndex(i => i.subtype === ItemType.Shipping);
      if (shipIndex >= 0 && !((items[shipIndex] instanceof ReceiptItem) || (items[shipIndex] instanceof ReturnItem))) {     // don't reemove shipping item for Receipt
        specialItems.push(items[shipIndex]);
        items.splice(shipIndex, 1);
      }
      const discountIndex = items.findIndex(i => i.subtype === ItemType.Discount);
      if (discountIndex >= 0) {
        specialItems.push(items[discountIndex]);
        items.splice(discountIndex, 1);
      }
      const taxIndex = items.findIndex(i => i.subtype === ItemType.SalesTax);
      if (taxIndex >= 0) {
        specialItems.push(items[taxIndex]);
        items.splice(taxIndex, 1);
      }
    }
    return specialItems;
  }

  /**
   * @desc merge special items into regular items; invoked before save or update
   */
  mergeSpecialItemsIntoRegularItems(regularItems: Array<PurchaseRequisitionItem | ReceiptItem | ReturnItem | VendorInvoiceItem | SalesOrderItem | PackingSlipItem | SalesReturnItem | CustomerInvoiceItem>,
                                    specialItems: Array<PurchaseRequisitionItem | ReceiptItem | ReturnItem | VendorInvoiceItem | SalesOrderItem | PackingSlipItem | SalesReturnItem | CustomerInvoiceItem>) {
    if (specialItems && specialItems.length > 0) {
      Object.assign(regularItems, regularItems.concat(specialItems));
    }
  }

  updateTaxForPrItem(prItem: PurchaseRequisitionItem, transaction: Transaction, salesTaxRate: number) {
    if (prItem) {
      prItem.taxAmount = 0;     // set default tax amount to zero and update it if taxable
      if (prItem.isTaxable) {
        if (prItem.qtyOrdered && transaction == Transaction.PurchaseRequisition) {
          prItem.taxAmount = prItem.unitCost * prItem.qtyOrdered * salesTaxRate / 100;
        } else if (prItem.qtyOrdered && transaction == Transaction.SalesOrder) {
          prItem.taxAmount = prItem.salesPrice * prItem.qtyOrdered * salesTaxRate / 100;
        } else if (prItem.qtyDelivered && transaction == Transaction.CustomerInvoice) {
          prItem.taxAmount = prItem.salesPrice * prItem.qtyDelivered * salesTaxRate / 100;
        } else if (prItem.qtyReceived && transaction == Transaction.Receipt) {
          prItem.taxAmount = prItem.unitCost * prItem.qtyReceived * salesTaxRate / 100;
        } else if (prItem.qtyReceived && transaction == Transaction.VendorInvoice) {
          prItem.taxAmount = prItem.actualUnitCost * prItem.qtyReceived * salesTaxRate / 100;
        }
        prItem.taxAmount = roundTo(prItem.taxAmount, RoundUpDecimalPlace);
      }
    }
  }

  updateTaxForItem(item: PurchaseRequisitionItem | ReturnItem | VendorInvoiceItem | SalesOrderItem | CustomerInvoiceItem | SalesReturnItem,
                   discountPercent: number, salesTaxRate: number) {

    if (item && item.isTaxable) {
      if (item instanceof ReturnItem || item instanceof SalesReturnItem) {
        item.taxAmount = (item.creditAmount - ((item.creditAmount * discountPercent) / 100)) * salesTaxRate / 100;
      } else {
        item.taxAmount = (item.extendedPrice - ((item.extendedPrice * discountPercent) / 100)) * salesTaxRate / 100;
      }
      item.taxAmount = roundTo(item.taxAmount, RoundUpDecimalPlace);
    }
  }

  updateExtendedPriceForPrItem(prItem: PurchaseRequisitionItem, transaction: Transaction) {
    if (transaction == Transaction.PurchaseRequisition && prItem.subtype != ItemType.Shipping && prItem.subtype != ItemType.Discount) {
      prItem.extendedPrice = prItem.qtyOrdered * prItem.unitCost;
    } else if (transaction == Transaction.Receipt && prItem.subtype != ItemType.Shipping && prItem.subtype != ItemType.Discount) {
      prItem.extendedPrice = prItem.qtyReceived * prItem.actualUnitCost;
    } else if (transaction == Transaction.VendorInvoice && prItem.subtype != ItemType.Shipping && prItem.subtype != ItemType.Discount) {
      prItem.extendedPrice = prItem.qtyReceived * prItem.actualUnitCost;
    } else if (transaction == Transaction.BillOfMaterial && prItem.subtype != ItemType.Shipping && prItem.subtype != ItemType.Discount) {
      prItem.extendedPrice = prItem.qtyOrdered * prItem.unitCost;
    } else if (transaction == Transaction.SalesOrder && prItem.subtype != ItemType.Shipping && prItem.subtype != ItemType.Discount) {
      prItem.extendedPrice = prItem.qtyOrdered * prItem.salesPrice;
    } else if (transaction == Transaction.CustomerInvoice && prItem.subtype != ItemType.Shipping && prItem.subtype != ItemType.Discount) {
      prItem.extendedPrice = prItem.qtyDelivered * prItem.salesPrice;
    }

    if (prItem.extendedPrice != null) {
      prItem.extendedPrice = roundTo(prItem.extendedPrice, RoundUpDecimalPlace);
    }
  }

  updateExtendedPriceForItem(item: PurchaseRequisitionItem | ReturnItem | VendorInvoiceItem | SalesOrderItem | CustomerInvoiceItem | SalesReturnItem) {
    if (item) {
      if (item instanceof PurchaseRequisitionItem) {
        item.extendedPrice = roundTo(item.qtyOrdered * item.unitCost, RoundUpDecimalPlace);
      } else if (item instanceof VendorInvoiceItem) {
        item.extendedPrice = roundTo(item.qtyReceived * item.actualUnitPrice, RoundUpDecimalPlace);
      } else if (item instanceof SalesOrderItem) {
        item.extendedPrice = roundTo(item.qtyOrdered * item.salesPrice, RoundUpDecimalPlace);
      } else if (item instanceof CustomerInvoiceItem) {
        item.extendedPrice = roundTo(item.qtyDelivered * item.salesPrice, RoundUpDecimalPlace);
      } else if (item instanceof ReturnItem) {
        item.creditAmount = roundTo(item.qtyReturned * item.unitCost, RoundUpDecimalPlace);
      } else if (item instanceof SalesReturnItem) {
        item.creditAmount = roundTo(item.qtyReturned * item.salesPrice, RoundUpDecimalPlace);
      } else {
        console.error("cannot determine instanceof item");
        console.log(item);
      }
    }
  }

  updateDiscountItem(discountItem: PurchaseRequisitionItem | ReturnItem | VendorInvoiceItem | SalesOrderItem | CustomerInvoiceItem | SalesReturnItem,
                     discountPercent: number, itemCreditAmountOrExtendedPrice: number) {
    if (discountItem) {
      if (discountItem instanceof ReturnItem || discountItem instanceof SalesReturnItem) {
        discountItem.creditAmount = itemCreditAmountOrExtendedPrice * discountPercent / 100;
        discountItem.creditAmount = roundTo(discountItem.creditAmount, RoundUpDecimalPlace);
      } else {
        discountItem.extendedPrice = itemCreditAmountOrExtendedPrice * discountPercent / 100;
        discountItem.extendedPrice = roundTo(discountItem.extendedPrice, RoundUpDecimalPlace);
      }
    }
  }

  /**
   * @desc set sales tax depends on the item type. the sum of regular items taxAmount is the sales tax extended price and also unitCost/actualUnitPrice/salesPrice
   \   */
  updateSalesTaxItem(regularItems: Array<PurchaseRequisitionItem | ReturnItem | VendorInvoiceItem | SalesOrderItem | CustomerInvoiceItem | SalesReturnItem>,
                     specialItems: Array<PurchaseRequisitionItem | ReturnItem | VendorInvoiceItem | SalesOrderItem | CustomerInvoiceItem | SalesReturnItem>) {

    let salesTaxItem = specialItems.find(item => item.subtype == ItemType.SalesTax);

    if (regularItems && salesTaxItem) {
      if (salesTaxItem instanceof ReturnItem || salesTaxItem instanceof SalesReturnItem) {
        salesTaxItem.creditAmount = regularItems.map(item => item.taxAmount).reduce((prev, curr) => prev + curr, 0);
        salesTaxItem.creditAmount = roundTo(salesTaxItem.creditAmount, RoundUpDecimalPlace);
      } else {
        salesTaxItem.extendedPrice = regularItems.map(item => item.taxAmount).reduce((prev, curr) => prev + curr, 0);
        salesTaxItem.extendedPrice = roundTo(salesTaxItem.extendedPrice, RoundUpDecimalPlace);
      }

      if (salesTaxItem instanceof PurchaseRequisitionItem) {
        salesTaxItem.unitCost = salesTaxItem.extendedPrice;
      } else if (salesTaxItem instanceof ReturnItem) {
        salesTaxItem.unitCost = salesTaxItem.creditAmount;
      } else if (salesTaxItem instanceof VendorInvoiceItem) {
        salesTaxItem.actualUnitPrice = salesTaxItem.extendedPrice;
      } else if (salesTaxItem instanceof SalesOrderItem || salesTaxItem instanceof CustomerInvoiceItem) {
        salesTaxItem.salesPrice = salesTaxItem.extendedPrice;
      } else if (salesTaxItem instanceof SalesReturnItem) {
        salesTaxItem.salesPrice = salesTaxItem.creditAmount;
      }
    } else {
      console.warn(`salesTaxItem could not be updated because either items or salesTaxItem could not be found.`);
    }
  }

  /**
   * @desc add sales tax item if there is no sales tax item in specialItems
   */
  addSalesTaxItem(regularItems: Array<PurchaseRequisitionItem | VendorInvoiceItem | SalesOrderItem | CustomerInvoiceItem | ReturnItem>,
                  specialItems: Array<PurchaseRequisitionItem | VendorInvoiceItem | SalesOrderItem | CustomerInvoiceItem | ReturnItem>, transaction: Transaction) {

    if (specialItems.find(item => item.subtype == ItemType.SalesTax)) {
      console.error("salesTaxItem cannot be added because it already exists in the specialItems");
      return;
    }
    if (!this.cache.salesTaxItem) {
      console.error("salesTaxItem cannot be added because it does not exists in the cache");
      return;
    }

    let salesTaxItem = this.newItemForTransaction(transaction);

    salesTaxItem.abstractItemId = this.cache.salesTaxItem.id;
    salesTaxItem.nameOrNumber = this.cache.salesTaxItem.nameOrNumber;
    salesTaxItem.subtype = this.cache.salesTaxItem.subtype;
    salesTaxItem.isTaxable = false;
    salesTaxItem.extendedPrice = 0;
    salesTaxItem.taxAmount = 0;

    if (salesTaxItem instanceof PurchaseRequisitionItem) {
      salesTaxItem.qtyOrdered = 1;
      salesTaxItem.unitCost = 0;
      salesTaxItem.unitOfMeasureId = this.cache.salesTaxItem.unitOfMeasureId;
      salesTaxItem.unitOfMeasureDescription = this.cache.salesTaxItem.unitOfMeasureDescription;
      salesTaxItem.fundingType = this.cache.salesTaxItem.fundingType;

    } else if (salesTaxItem instanceof VendorInvoiceItem) {
      salesTaxItem.qtyReceived = 1;
      salesTaxItem.unitCost = 0;
      salesTaxItem.actualUnitPrice = 0;

      salesTaxItem.unitOfMeasureId = this.cache.salesTaxItem.unitOfMeasureId;
      salesTaxItem.unitOfMeasureDescription = this.cache.salesTaxItem.unitOfMeasureDescription;
      salesTaxItem.fundingType = this.cache.salesTaxItem.fundingType;

    } else if (salesTaxItem instanceof SalesOrderItem) {
      salesTaxItem.qtyOrdered = 1;
      salesTaxItem.salesPrice = 0;

      salesTaxItem.unitOfMeasureId = this.cache.salesTaxItem.unitOfMeasureId;
      salesTaxItem.unitOfMeasureDescription = this.cache.salesTaxItem.unitOfMeasureDescription;
      salesTaxItem.fundingType = this.cache.salesTaxItem.fundingType;

    } else if (salesTaxItem instanceof CustomerInvoiceItem) {
      salesTaxItem.qtyDelivered = 1;
      salesTaxItem.salesPrice = 0;
      salesTaxItem.revenueType = RevenueType.Expense;    // revenueType applicable only in CustomerInvoice
      salesTaxItem.unitOfMeasureId = this.cache.salesTaxItem.unitOfMeasureId;
      salesTaxItem.unitOfMeasureDescription = this.cache.salesTaxItem.unitOfMeasureDescription;
      salesTaxItem.fundingType = this.cache.salesTaxItem.fundingType;
    } else {
      console.error("cannot determine instanceof salesTaxItem");
      console.log(salesTaxItem);
    }

    specialItems.push(salesTaxItem);
  }

  /**
   * @desc return how unit/sales cost/price stored in the item
   */
  getItemTypeSpecificUnitValue(item: PurchaseRequisitionItem | VendorInvoiceItem | SalesOrderItem | CustomerInvoiceItem | ReturnItem | SalesReturnItem): number {
    let itemSpecificUnitValue: number;

    if (item instanceof PurchaseRequisitionItem || item instanceof ReturnItem) {
      itemSpecificUnitValue = item.unitCost;
    } else if (item instanceof VendorInvoiceItem) {
      itemSpecificUnitValue = item.actualUnitPrice;
    } else if (item instanceof SalesOrderItem || item instanceof SalesReturnItem) {
      itemSpecificUnitValue = item.salesPrice;
    } else if (item instanceof CustomerInvoiceItem) {
      itemSpecificUnitValue = item.salesPrice;
    }
    return itemSpecificUnitValue;
  }

  /**
   * @desc update extended price, tax and charge code allocation on regular items
   */
  updateExtendedPriceAndSalesTax(regularItems: Array<PurchaseRequisitionItem | ReturnItem | VendorInvoiceItem | SalesOrderItem | CustomerInvoiceItem | SalesReturnItem>,
                                 specialItems: Array<PurchaseRequisitionItem | ReturnItem | VendorInvoiceItem | SalesOrderItem | CustomerInvoiceItem | SalesReturnItem>,
                                 salesTaxRate: number) {

    let shippingItem = specialItems.find(item => item.subtype == ItemType.Shipping);
    let discountItem = specialItems.find(item => item.subtype == ItemType.Discount);
    let discountPercent = discountItem == null ? 0 : this.getItemTypeSpecificUnitValue(discountItem);

    let creditOrExtendedPriceTotal = 0;
    for (let item of regularItems) {
      this.updateExtendedPriceForItem(item);

      if (item instanceof ReturnItem || item instanceof SalesReturnItem) {
        creditOrExtendedPriceTotal += item.creditAmount;
      } else {
        creditOrExtendedPriceTotal += item.extendedPrice;
      }

      this.updateTaxForItem(item, discountPercent, salesTaxRate);
      this.updateChargeCodeAllocation(item);
    }

    this.updateExtendedPriceForItem(shippingItem);            // re-calculate just in case backend does not set extended price for shipping
    this.updateDiscountItem(discountItem, discountPercent, creditOrExtendedPriceTotal);
    this.updateSalesTaxItem(regularItems, specialItems);
  }


  /**
   * @desc if there is at least one taxable item and if there is no sales tax item, it added one to special items
   *       if there is no taxable item and if there is sales tax item, remove it from the special items
   */
  addOrDeleteSalesTaxItem(regularItems: Array<PurchaseRequisitionItem | ReturnItem | VendorInvoiceItem | SalesOrderItem | CustomerInvoiceItem>,
                          specialItems: Array<PurchaseRequisitionItem | ReturnItem | VendorInvoiceItem | SalesOrderItem | CustomerInvoiceItem>, transaction: Transaction, includeTaxAmount?: boolean) {

    let taxableItemIndex;
    if (includeTaxAmount) {
      taxableItemIndex = regularItems.findIndex(item => item.isTaxable && item.taxAmount > 0);
    } else {
      taxableItemIndex = regularItems.findIndex(item => item.isTaxable);
    }
    let containsTaxableItem = taxableItemIndex >= 0;
    let salesTaxItemIndex = specialItems.findIndex(item => item.subtype == ItemType.SalesTax);
    let containsSalesTaxItem = salesTaxItemIndex >= 0;

    if (containsTaxableItem && !containsSalesTaxItem) {
      // add sales tax item
      this.addSalesTaxItem(regularItems, specialItems, transaction);
    } else if (!containsTaxableItem && containsSalesTaxItem) {
      // remove sales tax item
      specialItems.splice(salesTaxItemIndex, 1);
    }
  }

  /**
   * @desc update charge code allocation for item including allocation amount and tax
   */
  updateChargeCodeAllocation(item: PurchaseRequisitionItem | ReturnItem | VendorInvoiceItem | SalesOrderItem | CustomerInvoiceItem | SalesReturnItem) {

    let chargeCodes: Array<PurchaseChargeCode | PurchaseReturnChargeCode | VendorInvoiceChargeCode>;

    if (item instanceof PurchaseRequisitionItem) {
      chargeCodes = item.purchaseChargeCodes;
    } else if (item instanceof ReturnItem) {
      chargeCodes = item.returnChargeCodes;
    } else if (item instanceof VendorInvoiceItem) {
      chargeCodes = item.invoiceChargeCodes;
    } else if (item instanceof SalesOrderItem) {
      console.debug(`sales order items do not have charge code. returning...`);
      return;
    } else if (item instanceof CustomerInvoiceItem) {
      console.error(`customer invoice item does not have charge code. returning...`);
      return;
    }

    if (chargeCodes && chargeCodes.length > 0) {
      let totalAllocationAmount = 0;
      let totalTaxAmount = 0;
      for (let cc of chargeCodes) {
        if (item instanceof ReturnItem || item instanceof SalesReturnItem) {
          cc.allocationAmount = roundTo(item.creditAmount * (cc.allocationPercent / 100), RoundUpDecimalPlace);
        } else {
          cc.allocationAmount = roundTo(item.extendedPrice * (cc.allocationPercent / 100), RoundUpDecimalPlace);
        }
        cc.taxAmount = roundTo(item.taxAmount * (cc.allocationPercent / 100), RoundUpDecimalPlace);
        totalAllocationAmount += cc.allocationAmount;
        totalTaxAmount += cc.taxAmount;
      }

      // apply rounding error e.g. item.extendedPrice = 299.95 and 50% allocation to two charge numbers sso-6616
      let diffAllocationAmount;
      if (item instanceof ReturnItem || item instanceof SalesReturnItem) {
        diffAllocationAmount = item.creditAmount - totalAllocationAmount;
      } else {
        diffAllocationAmount = item.extendedPrice - totalAllocationAmount;
      }
      diffAllocationAmount = roundTo(diffAllocationAmount, RoundUpDecimalPlace);
      if (diffAllocationAmount != 0) {
        console.log(`adjusting allocation amount from ${chargeCodes[0].allocationAmount} to ${chargeCodes[0].allocationAmount}+${diffAllocationAmount}`);
        chargeCodes[0].allocationAmount += diffAllocationAmount;
      }

      let diffTaxAmount = roundTo(item.taxAmount - totalTaxAmount, RoundUpDecimalPlace);
      if (diffTaxAmount != 0) {
        console.log(`adjusting tax amount from ${chargeCodes[0].taxAmount} to ${chargeCodes[0].taxAmount}+${diffTaxAmount}`);
        chargeCodes[0].taxAmount += diffTaxAmount;
        chargeCodes[0].taxAmount = roundTo(chargeCodes[0].taxAmount, RoundUpDecimalPlace);
      }
    }

  }

  /**
   * @desc add new item to either regularItems or specialItems
   * !!!!! must call a spread operation on regularItems in order to force change detection when adding an item to an array
   */
  addItemToRegularOrSpecialItems(newItem: PurchaseRequisitionItem | VendorInvoiceItem | SalesOrderItem | CustomerInvoiceItem,
                                 regularItems: Array<PurchaseRequisitionItem | VendorInvoiceItem | SalesOrderItem | CustomerInvoiceItem>,
                                 specialItems: Array<PurchaseRequisitionItem | VendorInvoiceItem | SalesOrderItem | CustomerInvoiceItem>,
                                 transaction: Transaction, salesTaxRate: number) {

    if (this.isUnsupportedTransaction(transaction)) {
      console.error(`unsupported transaction. returning...`);
      return;
    }

    if (this.isSpecialItem(newItem)) {  // handle special item
      if (this.getItemTypeSpecificUnitValue(newItem) <= 0 && (newItem.subtype == ItemType.Discount || newItem.subtype == ItemType.Shipping)) {
        this.growl.add(GrowlType.Warn, `${newItem.subtype} item with 0 or less value cannot be added`);
        return;
      }

      specialItems.push(newItem);
      console.log(`added item (id=${newItem.id} name=${newItem.nameOrNumber}) to specialItems`);

      if (newItem.subtype == ItemType.Discount) {
        this.updateExtendedPriceAndSalesTax(regularItems, specialItems, salesTaxRate);
      }
    } else {                            // handle regular item
      regularItems.push(newItem);
      console.log(`added item (id=${newItem.id} name=${newItem.nameOrNumber}) to regularItems`);

      this.addOrDeleteSalesTaxItem(regularItems, specialItems, transaction);
      this.updateExtendedPriceAndSalesTax(regularItems, specialItems, salesTaxRate);
    }
  }

  /**
   * @desc update item in either regularItems or specialItems; if discount item value is 0, remove it from the special items
   */
  updateItemInRegularOrSpecialItems(updatedItem: PurchaseRequisitionItem | VendorInvoiceItem | SalesOrderItem | CustomerInvoiceItem | ReturnItem,
                                    pointerToItem: PurchaseRequisitionItem | VendorInvoiceItem | SalesOrderItem | CustomerInvoiceItem | ReturnItem,
                                    regularItems: Array<PurchaseRequisitionItem | VendorInvoiceItem | SalesOrderItem | CustomerInvoiceItem | ReturnItem>,
                                    specialItems: Array<PurchaseRequisitionItem | VendorInvoiceItem | SalesOrderItem | CustomerInvoiceItem | ReturnItem>,
                                    transaction: Transaction, salesTaxRate: number) {

    if (this.getItemTypeSpecificUnitValue(updatedItem) <= 0 && (updatedItem.subtype == ItemType.Discount || updatedItem.subtype == ItemType.Shipping)) {
      let index = specialItems.findIndex(item => item == pointerToItem);
      if (index >= 0) {
        specialItems.splice(index, 1);
        console.debug(`${updatedItem.subtype} item with 0 or less value removed`);
      }
    } else {
      Object.assign(pointerToItem, updatedItem);
      if (updatedItem instanceof ReturnItem) {
        console.debug(`updated item (id=${updatedItem.id} name=${updatedItem.abstractItemDetail.nameOrNumber})`);
      } else {
        console.debug(`updated item (id=${updatedItem.id} name=${updatedItem.nameOrNumber})`);
      }
    }

    this.addOrDeleteSalesTaxItem(regularItems, specialItems, transaction);
    this.updateExtendedPriceAndSalesTax(regularItems, specialItems, salesTaxRate);    // any quantity or price change will impact discount and sales tax item
  }

  /**
   * @desc add new item to either regularItems or specialItems
   */
  deleteItemInRegularOrSpecialItems(itemToDelete: PurchaseRequisitionItem | VendorInvoiceItem | SalesOrderItem | CustomerInvoiceItem,
                                    regularItems: Array<PurchaseRequisitionItem | VendorInvoiceItem | SalesOrderItem | CustomerInvoiceItem>,
                                    specialItems: Array<PurchaseRequisitionItem | VendorInvoiceItem | SalesOrderItem | CustomerInvoiceItem>,
                                    salesTaxRate: number) {

    if (this.isSpecialItem(itemToDelete)) {
      let index = specialItems.findIndex(item => item == itemToDelete);
      if (index >= 0) {
        specialItems.splice(index, 1);
        console.log(`deleted item (id=${itemToDelete.id} name=${itemToDelete.nameOrNumber}) from specialItems`);
      } else {
        this.growl.add(GrowlType.Error, `could not delete the item (id=${itemToDelete.id} name=${itemToDelete.nameOrNumber})`);
      }
    } else {
      let index = regularItems.findIndex(item => item == itemToDelete);
      if (index >= 0) {
        regularItems.splice(index, 1);
        console.log(`deleted item (id=${itemToDelete.id} name=${itemToDelete.nameOrNumber}) from regularItems`);
      } else {
        this.growl.add(GrowlType.Error, `could not delete the item (id=${itemToDelete.id} name=${itemToDelete.nameOrNumber})`);
      }
    }
    this.updateExtendedPriceAndSalesTax(regularItems, specialItems, salesTaxRate);    // deleting an item will impact discount and sales tax item
  }

  /**
   * @desc calculates table footer values including subtotal (customer invoice only), shipping, discount, tax and total
   */
  //TODO: Check passing an additional parameter vs adding a new transaction enum
  calculateSummary(regularItems: Array<PurchaseRequisitionItem | VendorInvoiceItem | SalesOrderItem | CustomerInvoiceItem | ReturnItem | SalesReturnItem>,
                   specialItems: Array<PurchaseRequisitionItem | VendorInvoiceItem | SalesOrderItem | CustomerInvoiceItem | ReturnItem | SalesReturnItem>,
                   transaction: Transaction): TableFooterSummary {

    let tableFooterSummary = new TableFooterSummary();
    tableFooterSummary.subTotal = 0;

    if (regularItems && regularItems.length > 0) {
      for (let item of regularItems) {
        if (item instanceof ReturnItem || item instanceof SalesReturnItem) {
          tableFooterSummary.subTotal = tableFooterSummary.subTotal + item.creditAmount;
        } else if (!(item instanceof CustomerInvoiceItem)) {
          tableFooterSummary.subTotal = tableFooterSummary.subTotal + item.extendedPrice;
        } else if ((item instanceof CustomerInvoiceItem) && transaction == Transaction.ManualCustomerInvoice) {
          tableFooterSummary.subTotal = tableFooterSummary.subTotal + item.extendedPrice;
        }
      }
    }
    tableFooterSummary.subTotal = roundTo(tableFooterSummary.subTotal, RoundUpDecimalPlace);

    tableFooterSummary.taxes = 0;

    if (specialItems) { // calculate tax, shipping and discount
      let salesTaxItem = specialItems.find(item => item.subtype == ItemType.SalesTax);
      if (salesTaxItem) {
        if (salesTaxItem instanceof ReturnItem || salesTaxItem instanceof SalesReturnItem) {
          tableFooterSummary.taxes = salesTaxItem.creditAmount;
        } else {
          tableFooterSummary.taxes = salesTaxItem.extendedPrice;
        }
      }

      let discountItem = specialItems.find(item => item.subtype == ItemType.Discount);
      if (discountItem) {
        tableFooterSummary.discountPercent = this.getItemTypeSpecificUnitValue(discountItem);
        if (discountItem instanceof ReturnItem || discountItem instanceof SalesReturnItem) {
          tableFooterSummary.regularDiscountAmount = discountItem.creditAmount;
        } else {
          tableFooterSummary.regularDiscountAmount = discountItem.extendedPrice;
        }
      }

      let shippingItem = specialItems.find(item => item.subtype == ItemType.Shipping);
      if (shippingItem) {
        tableFooterSummary.shipping = this.getItemTypeSpecificUnitValue(shippingItem);
      }
    }

    tableFooterSummary.total = 0;
    if (tableFooterSummary.subTotal) {
      tableFooterSummary.total += tableFooterSummary.subTotal;
    }
    if (tableFooterSummary.shipping) {
      tableFooterSummary.total += tableFooterSummary.shipping;
    }
    if (tableFooterSummary.taxes) {
      tableFooterSummary.total += tableFooterSummary.taxes;
    }
    if (tableFooterSummary.regularDiscountAmount) {
      tableFooterSummary.total -= tableFooterSummary.regularDiscountAmount;
    }

    tableFooterSummary.total = roundTo(tableFooterSummary.total, RoundUpDecimalPlace);

    return tableFooterSummary;
  }


  /**
   * @summary on add/edit from add-line-item common component -  this is the 1st check that should happen. param: otherTableItems is applicable for Customer Invoice only.
   * @desc checks for duplicate when user is trying to add.
   * @param newItem new item which is being checked against
   * @param specialItems special items such as discount and shipping
   * @param tableItems original table items
   * @param otherTableItems only used in Customer Invoice as there are 2 tabs in which items are divided . Optional field
   *
   */
  isItemUnique(newItem: PurchaseRequisitionItem, tableItems: any[], specialItems?: any[], otherTableItems?: any[]): boolean {

    let index = -1;

    if (tableItems) {
      if (newItem.subtype == ItemType.Service) {
        for (const item of tableItems) {
          if (this.isSameItem(newItem, item)) {
            if (this.itemsHaveSameChargeCodes(newItem, item)) {
              index = 0;
              break;
            }
          }
        }
      } else {
        index = tableItems.findIndex(item => this.isSameItem(newItem, item));
      }
    }

    if (index < 0 && specialItems && this.isSpecialItem(newItem)) {
      index = specialItems.findIndex(item => this.isSameItem(newItem, item));
    }

    if (index < 0 && otherTableItems) {
      index = otherTableItems.findIndex(item => this.isSameItem(newItem, item));
    }

    return index < 0;
  }

  isSameItem(itemA: PurchaseRequisitionItem, itemB: any): boolean {
    return itemA.nameOrNumber === itemB.nameOrNumber || (itemB.abstractItemDetail && (itemB.abstractItemDetail.nameOrNumber === itemA.nameOrNumber));
  }

  itemsHaveSameChargeCodes(itemA: PurchaseRequisitionItem, itemB: PurchaseRequisitionItem | VendorInvoiceItem): boolean {
    let chargeCodesToCompare : Array<PurchaseChargeCode> | Array<VendorInvoiceChargeCode> = itemB instanceof PurchaseRequisitionItem ? itemB.purchaseChargeCodes : itemB.invoiceChargeCodes;
    let haveSameChargeCodes = itemA.purchaseChargeCodes.length == chargeCodesToCompare.length;
    if (haveSameChargeCodes && itemA.purchaseChargeCodes.length != 0) {
      if(itemB instanceof  PurchaseRequisitionItem){
        for (const chargeCodeA of itemA.purchaseChargeCodes) {
          const haveSameChargeCode = itemB.purchaseChargeCodes.find(chargeCodeB => chargeCodeB.chargeCodeId == chargeCodeA.chargeCodeId);
          if (!haveSameChargeCode) {
            haveSameChargeCodes = false;
            break;
          }
        }
      } else if(itemB instanceof VendorInvoiceItem){
        for (const chargeCodeA of itemA.purchaseChargeCodes) {
          const haveSameChargeCode = itemB.invoiceChargeCodes.find(chargeCodeB => chargeCodeB.chargeCodeId == chargeCodeA.chargeCodeId);
          if (!haveSameChargeCode) {
            haveSameChargeCodes = false;
            break;
          }
        }
      }

    }
    return haveSameChargeCodes;
  }

  setSequenceNumber(regularItem: CustomerInvoiceItem | SalesOrderItem, regularItems: Array<CustomerInvoiceItem | SalesOrderItem>) {
    if (this.isSpecialItem(regularItem)) {
      return;
    }
    let nextNumber = this.getNextSequenceNumber(regularItems);
    regularItem.sequenceNum = nextNumber;
  }

  getNextSequenceNumber(regularItems: Array<CustomerInvoiceItem | SalesOrderItem>): number {
    let sequenceNumbers = regularItems.map(item => {
      return item.sequenceNum == null ? 0 : item.sequenceNum;
    });
    if (sequenceNumbers.length <= 0) {
      return 1;
    }
    let maxSequenceNumber = Math.max(...sequenceNumbers);

    if (isNaN(maxSequenceNumber)) {
      return 1;
    } else {
      return maxSequenceNumber + 1;
    }
  }

  initializeSequenceNumbers(regularItems: Array<CustomerInvoiceItem | SalesOrderItem>) {
    let itemsWithSequenceNumbers = regularItems.filter(it => !(it.sequenceNum == null));
    let itemsWithOutequenceNumbers = regularItems.filter(it => it.sequenceNum == null);
    if (itemsWithSequenceNumbers.length != itemsWithOutequenceNumbers.length) {
      this.resetSequenceNumbers(regularItems);
    }

  }

  resetSequenceNumbers(regularItems: Array<CustomerInvoiceItem | SalesOrderItem>) {
    if (regularItems.length <= 0) {
      return;
    }
    let sortedItems = regularItems.sort((a, b) => a.sequenceNum < b.sequenceNum ? -1 : a.sequenceNum > b.sequenceNum ? 1 : 0);
    let index = 1;
    sortedItems.forEach(item => {
      item.sequenceNum = index;
      index++;
    });
  }
}
